/**
 * Shared boot for the theme block scripts (blocks/*).
 *
 * Every block script keeps its behavior in one initializer, `initialize(root, options)`:
 * - Frontend: called once with the whole document after DOMContentLoaded (or right away when the document
 *   is already parsed), or after `load` for scripts that always waited for it.
 * - Block editor canvas: handed to the canvas runtime (inc/Blocks/block-canvas.js), which calls it with the
 *   preview root after every ACF preview render, with { isEditor: true, autoplay: false }.
 *
 * ACF re-renders previews by reconciling the same DOM, so initializers run repeatedly: bind through claim()
 * so each element is wired once, and remove window/document listeners when the block leaves the canvas.
 */
export function bootBlock(slug, initialize, { waitFor = 'DOMContentLoaded' } = {}) {
    if (window.juniperBlockRuntime) {
        window.juniperBlockRuntime.register(slug, initialize);
        return;
    }

    const run = () => initialize(document, { isEditor: false, autoplay: true });

    if (waitFor === 'load') {
        if (document.readyState === 'complete') {
            run();
        } else {
            window.addEventListener('load', run, { once: true });
        }
    } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
        run();
    }
}

/**
 * Frontend roots are the document, so this returns the selector itself and every library call keeps its
 * document-wide selector semantics. Editor roots are a single preview, so the selector is resolved inside it.
 */
export function scoped(root, selector) {
    return root.nodeType === Node.DOCUMENT_NODE ? selector : root.querySelector(selector);
}

/**
 * Marks an element as wired; returns false when it already was.
 */
export function claim(element, key = 'juniperReady') {
    if (!element || element.dataset[key] === 'true') {
        return false;
    }

    element.dataset[key] = 'true';

    return true;
}

/**
 * Adds a window/document listener that removes itself once `owner` has left the document. On the frontend
 * the owner is the document, which never leaves, so this is a plain addEventListener.
 */
export function listenWhileConnected(target, type, handler, owner, options) {
    const listener = (event) => {
        if (!owner.isConnected) {
            target.removeEventListener(type, listener, options);
            return;
        }

        handler(event);
    };

    target.addEventListener(type, listener, options);
}
